/* eslint-disable @typescript-eslint/no-explicit-any */

import type {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from './ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useEffect, useState } from 'react'
import { useDebounce } from '@/hooks/useDebounce'
import clsx from 'clsx'

const globalFilterFunction: FilterFn<any> = (row, columnId, filterValue) => {
  const cellValue = row.original[columnId]
  if (typeof cellValue === 'number') {
    return cellValue?.toString().includes(filterValue)
  }
  return cellValue?.toLowerCase().includes(String(filterValue).toLowerCase())
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  initialPageIndex?: number
  pageName?: string

  // 🔍 Recherche (optionnelle)
  onSearchChange?: (value: string) => void
  searchValue?: string
  autoCompleteOptions?: string[]

  // 🌍 Contrôle du mode (local ou distant)
  paginationServer?: boolean
  sortingServer?: boolean

  // 🔁 Pagination + tri contrôlés
  onPaginationChange?: (page: number) => void
  onPageChange?: (pageIndex: number) => void
  onPageSizeChange?: (pageSize: number) => void
  onSortChange?: (sorting: SortingState) => void
  currentPageIndex?: number
  currentPageSize?: number
  currentSorting?: SortingState
  total?: number
  pageCount?: number

  onRowClick?: (row: TData, index: number) => void
  rowClickable?: boolean

  getRowClassName?: (row: TData, index: number) => string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  initialPageIndex = 0,

  onSearchChange,
  searchValue,
  autoCompleteOptions,
  paginationServer = false,
  sortingServer = false,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  currentPageIndex,
  currentPageSize,
  currentSorting,
  pageCount,

  onRowClick,
  rowClickable = false,

  getRowClassName,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [globalFilter, setGlobalFilter] = useState('')
  const localDebounced = useDebounce(globalFilter, 500)
  const rowClass = rowClickable ? 'cursor-pointer hover:bg-muted/50' : undefined

  const table = useReactTable({
    data,
    columns,
    manualPagination: paginationServer,
    manualSorting: sortingServer,
    pageCount: paginationServer ? pageCount : undefined,

    getCoreRowModel: getCoreRowModel(),
    ...(sortingServer ? {} : { getSortedRowModel: getSortedRowModel() }),
    ...(!onSearchChange
      ? {
          getFilteredRowModel: getFilteredRowModel(),
          globalFilterFn: globalFilterFunction,
        }
      : {}),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: sortingServer
      ? (updaterOrValue) => {
          const value =
            typeof updaterOrValue === 'function'
              ? updaterOrValue(sorting)
              : updaterOrValue
          onSortChange?.(value)
        }
      : setSorting,
    onPaginationChange: paginationServer
      ? (updater) => {
          const updated =
            typeof updater === 'function'
              ? updater({
                  pageIndex: currentPageIndex ?? initialPageIndex,
                  pageSize: currentPageSize ?? 10,
                })
              : updater

          onPageChange?.(updated.pageIndex)
          onPageSizeChange?.(updated.pageSize)
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    globalFilterFn: globalFilterFunction,
    state: {
      sorting: sortingServer ? currentSorting ?? [] : sorting,
      pagination: {
        pageIndex: currentPageIndex ?? initialPageIndex,
        pageSize: currentPageSize ?? 10,
      },
      columnFilters,
      columnVisibility,
      globalFilter,
    },
  })

  useEffect(() => {
    if (!onSearchChange) {
      setGlobalFilter(localDebounced)
    }
  }, [localDebounced, onSearchChange])

  return (
    <>
      <div className='flex items-center py-4'>
        <div className='relative w-full max-w-sm mr-4'>
          <Input
            placeholder='Rechercher...'
            value={searchValue ?? globalFilter}
            onChange={(e) => {
              const value = e.target.value
              if (onSearchChange) {
                onSearchChange(value)
              } else {
                setGlobalFilter(value)
              }
            }}
          />
          {(autoCompleteOptions?.length ?? 0) > 0 && (
            <div className='absolute top-full mt-1 w-full z-10 bg-white dark:bg-background border rounded shadow-md max-h-64 overflow-y-auto hover:text-gray-500'>
              {autoCompleteOptions?.map((option, index) => (
                <div
                  key={index}
                  className='p-2 hover:bg-gray-100 cursor-pointer'
                  onClick={() => {
                    onSearchChange?.(option)
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='outline' className='ml-auto'>
              Colonne
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className='capitalize'
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                )
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className='rounded-md border mt-5'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const computedRowClass = getRowClassName?.(
                  row.original,
                  row.index
                )
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={clsx(rowClass, computedRowClass)}
                    onClick={
                      rowClickable && onRowClick
                        ? () => onRowClick(row.original, row.index)
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  Aucune Donnée Pour L'instant.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className='flex items-center justify-end px-2 py-2'>
        {/*<div className='flex-1 text-sm text-muted-foreground'>
          {table.getFilteredSelectedRowModel().rows.length} sur{' '}
          {table.getFilteredRowModel().rows.length} sélectionée(s).
            </div>*/}
        <div className='flex items-center space-x-6 lg:space-x-8'>
          <div className='flex items-center space-x-2'>
            <p className='text-sm font-medium'>Nbre/page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value: any) => {
                table.setPageSize(Number(value))
                if (onPageSizeChange) {
                  onPageSizeChange(Number(value))
                }
              }}
            >
              <SelectTrigger className='h-8 w-[70px]'>
                <SelectValue
                  placeholder={table.getState().pagination.pageSize}
                />
              </SelectTrigger>
              <SelectContent side='top'>
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='flex w-[100px] items-center justify-center text-sm font-medium'>
            Page {table.getState().pagination.pageIndex + 1} /{' '}
            {table.getPageCount()}
          </div>

          <div className='flex items-center space-x-2'>
            <Button
              variant='outline'
              className='hidden h-8 w-8 p-0 lg:flex'
              onClick={() => {
                const firstPageIndex = 0
                if (table.getCanPreviousPage()) {
                  onPageChange?.(firstPageIndex)
                }
              }}
              disabled={!table.getCanPreviousPage()}
            >
              <span className='sr-only'>Go to first page</span>
              <ChevronsLeft className='h-4 w-4' />
            </Button>
            <Button
              variant='outline'
              className='h-8 w-8 p-0'
              onClick={() => {
                const previousPageIndex =
                  table.getState().pagination.pageIndex - 1
                if (table.getCanPreviousPage()) {
                  onPageChange?.(previousPageIndex)
                }
              }}
              disabled={!table.getCanPreviousPage()}
            >
              <span className='sr-only'>Go to previous page</span>
              <ChevronLeftIcon className='h-4 w-4' />
            </Button>
            <Button
              variant='outline'
              className='h-8 w-8 p-0'
              onClick={() => {
                const nextPageIndex = table.getState().pagination.pageIndex + 1
                if (table.getCanNextPage()) {
                  onPageChange?.(nextPageIndex)
                }
              }}
              disabled={!table.getCanNextPage()}
            >
              <span className='sr-only'>Go to next page</span>
              <ChevronRightIcon className='h-4 w-4' />
            </Button>
            <Button
              variant='outline'
              className='hidden h-8 w-8 p-0 lg:flex'
              onClick={() => {
                const lastPageIndex = table.getPageCount() - 1
                if (table.getCanNextPage()) {
                  onPageChange?.(lastPageIndex)
                }
              }}
              disabled={!table.getCanNextPage()}
            >
              <span className='sr-only'>Go to last page</span>
              <ChevronsRight className='h-4 w-4' />
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
